import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasAnyPermission, getUserPermissions, isAdmin } from '../utils/permissions';
import { ShieldAlert } from 'lucide-react';

/**
 * PermissionGuard Component
 * Wraps routes/components to check if user has required permissions
 */
const PermissionGuard = ({ 
  children, 
  requiredPermissions = [], 
  fallback = null,
  showMessage = true 
}) => {
  const { user } = useAuth();

  // If user is admin, grant access
  if (isAdmin(user)) {
    return <>{children}</>;
  }

  // Get user permissions
  const userPermissions = getUserPermissions(user);

  // Check if user has required permissions
  const hasAccess = hasAnyPermission(userPermissions, requiredPermissions);

  if (!hasAccess) {
    // If custom fallback provided, use it
    if (fallback) {
      return <>{fallback}</>;
    }

    // Show default permission denied message
    if (showMessage) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 rounded-full p-4">
                <ShieldAlert className="w-12 h-12 text-red-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to access this module. Please contact the administrator to get access.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 font-medium mb-2">Required Permissions:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {requiredPermissions.map((perm, index) => (
                  <span
                    key={index}
                    className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-mono"
                  >
                    {perm}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    // If no message and no fallback, redirect to home
    return <Navigate to="/pos" replace />;
  }

  return <>{children}</>;
};

/**
 * PermissionCheck Component
 * For conditional rendering within components
 * Usage: <PermissionCheck requiredPermissions={['menu.edit']}>...</PermissionCheck>
 */
export const PermissionCheck = ({ 
  children, 
  requiredPermissions = [], 
  fallback = null 
}) => {
  const { user } = useAuth();

  // If user is admin, show content
  if (isAdmin(user)) {
    return <>{children}</>;
  }

  // Get user permissions
  const userPermissions = getUserPermissions(user);

  // Check if user has required permissions
  const hasAccess = hasAnyPermission(userPermissions, requiredPermissions);

  if (!hasAccess) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
};

export default PermissionGuard;
